import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Inbox, Mail, PhoneCall, PhoneMissed, Voicemail } from 'lucide-react';
import { crmApi, type ApiRecord } from '../../lib/api';

type Kind = 'all' | 'email' | 'call';

function relTime(iso: string) {
	const d = new Date(iso);
	const diff = Date.now() - d.getTime();
	const m = Math.round(diff / 60_000);
	if (m < 1) return 'just now';
	if (m < 60) return `${m}m ago`;
	const h = Math.round(m / 60);
	if (h < 24) return `${h}h ago`;
	const days = Math.round(h / 24);
	if (days < 30) return `${days}d ago`;
	return d.toLocaleDateString();
}

export function InboxPage() {
	const [kind, setKind] = useState<Kind>('all');
	const [vendorId, setVendorId] = useState<number | 'all' | 'untagged'>('all');

	const inboxQuery = useQuery({
		queryKey: ['inbox-unified'],
		queryFn: () => crmApi.inboxUnified({ limit: '100' }),
		refetchInterval: 30_000
	});
	const businessUnitsQuery = useQuery({ queryKey: ['business-units'], queryFn: crmApi.businessUnits });
	const items = (inboxQuery.data?.items ?? []) as ApiRecord[];
	const units = businessUnitsQuery.data ?? [];

	const filtered = useMemo(() => {
		return items.filter((it) => {
			if (kind !== 'all' && String(it.kind) !== kind) return false;
			if (vendorId === 'untagged' && it.vendor_id != null) return false;
			if (typeof vendorId === 'number' && Number(it.vendor_id) !== vendorId) return false;
			return true;
		});
	}, [items, kind, vendorId]);

	const counts = useMemo(() => {
		const byVendor = new Map<number, number>();
		let untagged = 0;
		let email = 0;
		let call = 0;
		for (const it of items) {
			if (it.kind === 'email') email++;
			if (it.kind === 'call') call++;
			if (it.vendor_id == null) untagged++;
			else {
				const k = Number(it.vendor_id);
				byVendor.set(k, (byVendor.get(k) ?? 0) + 1);
			}
		}
		return { byVendor, untagged, email, call, total: items.length };
	}, [items]);

	return (
		<div className="space-y-3">
			<header className="rounded-2xl border border-subtle bg-surface-2 px-4 py-3 shadow-panel">
				<p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-accent">Inbox</p>
				<h1 className="mt-1 text-xl font-semibold text-primary">Inbound across every channel</h1>
				<p className="mt-0.5 text-xs text-muted">
					Live feed of inbound email (MailBear) and calls (Talkadillo). Routing rules tag each item with a business unit so the right rep sees what's theirs.
				</p>
				<div className="mt-3 flex flex-wrap gap-1.5">
					<FilterChip active={kind === 'all'} onClick={() => setKind('all')}>
						All <Pill n={counts.total} />
					</FilterChip>
					<FilterChip active={kind === 'email'} onClick={() => setKind('email')}>
						<Mail className="h-3 w-3" /> Email <Pill n={counts.email} />
					</FilterChip>
					<FilterChip active={kind === 'call'} onClick={() => setKind('call')}>
						<PhoneCall className="h-3 w-3" /> Calls <Pill n={counts.call} />
					</FilterChip>
				</div>
				<div className="mt-2 flex flex-wrap gap-1.5">
					<FilterChip active={vendorId === 'all'} onClick={() => setVendorId('all')}>All BUs</FilterChip>
					<FilterChip active={vendorId === 'untagged'} onClick={() => setVendorId('untagged')}>
						Untagged <Pill n={counts.untagged} />
					</FilterChip>
					{units.map((u) => {
						const id = Number(u.id);
						return (
							<FilterChip key={id} active={vendorId === id} onClick={() => setVendorId(id)}>
								{String(u.name)} <Pill n={counts.byVendor.get(id) ?? 0} />
							</FilterChip>
						);
					})}
				</div>
			</header>

			<div className="rounded-2xl border border-subtle bg-surface-2 p-3 shadow-panel">
				{inboxQuery.isLoading ? (
					<div className="px-3 py-4 text-sm text-muted">Loading inbox…</div>
				) : filtered.length === 0 ? (
					<div className="flex items-center gap-3 rounded-xl border border-dashed border-subtle px-3 py-4 text-sm text-muted">
						<Inbox className="h-4 w-4 text-muted" />
						Nothing matches your filter. Inbound webhooks need <code className="rounded bg-surface-2 px-1 text-[10px] text-accent">webhooks.mailbear_secret</code> / <code className="rounded bg-surface-2 px-1 text-[10px] text-accent">webhooks.talkadillo_secret</code> set in config.
					</div>
				) : (
					<ul className="divide-y divide-subtle">
						{filtered.map((it) => (
							<InboxRow key={`${String(it.kind)}-${String(it.id)}`} item={it} />
						))}
					</ul>
				)}
			</div>
		</div>
	);
}

function InboxRow({ item }: { item: ApiRecord }) {
	const isEmail = item.kind === 'email';
	const status = String(item.status ?? '');
	const callIcon = status === 'voicemail' ? Voicemail : status === 'no_answer' ? PhoneMissed : PhoneCall;
	const Icon = isEmail ? Mail : callIcon;
	const iconTone = isEmail
		? 'bg-sky-500/10 text-sky-300'
		: status === 'no_answer' || status === 'voicemail'
			? 'bg-amber-500/10 text-amber-300'
			: 'bg-emerald-500/10 text-emerald-300';

	const title = isEmail
		? String(item.subject ?? '(no subject)')
		: status === 'voicemail'
			? 'Voicemail'
			: status === 'no_answer'
				? 'Missed call'
				: `Call · ${status || 'inbound'}`;

	return (
		<li className="flex items-start gap-3 py-2.5">
			<div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${iconTone}`}>
				<Icon className="h-4 w-4" />
			</div>
			<div className="min-w-0 flex-1">
				<div className="flex items-start justify-between gap-2">
					<p className="truncate text-sm font-medium text-primary">{title}</p>
					<span className="shrink-0 text-[10px] text-muted">{relTime(String(item.occurred_at))}</span>
				</div>
				<p className="truncate text-xs text-muted">
					{String(item.contact_name ?? item.from ?? '—')}
					{item.company_name ? ` · ${String(item.company_name)}` : ''}
				</p>
				{item.snippet ? <p className="mt-1 truncate text-[11px] text-muted">{String(item.snippet)}</p> : null}
				<div className="mt-1.5 flex flex-wrap gap-1">
					{item.vendor_name ? (
						<span className="rounded-full bg-accent/10 px-2 py-0.5 text-[10px] text-accent">{String(item.vendor_name)}</span>
					) : (
						<span className="rounded-full bg-slate-500/10 px-2 py-0.5 text-[10px] text-muted">untagged</span>
					)}
					{!isEmail && status ? (
						<span className="rounded-full bg-surface-2 px-2 py-0.5 text-[10px] text-secondary">{status}</span>
					) : null}
				</div>
			</div>
		</li>
	);
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
	return (
		<button
			onClick={onClick}
			className={`inline-flex items-center gap-1 rounded-xl px-3 py-1.5 text-xs transition ${
				active ? 'bg-accent text-accent-contrast shadow-panel' : 'border border-subtle bg-surface-2 text-secondary hover:bg-surface-hover'
			}`}
		>
			{children}
		</button>
	);
}

function Pill({ n }: { n: number }) {
	return <span className="ml-1 rounded-full bg-surface-2/40 px-1.5 py-0.5 text-[10px]">{n}</span>;
}
